import * as Sentry from "@sentry/browser";
import { AxiosRequestConfig } from "axios";
import { apiAddress } from "../../config/server";
import { IAssociation } from "../../types/association/Association";
import { IFuzzyTally } from "../../types/imports/ImportPerson";
import { IPerson, IPersonAssociation } from "../../types/persons/person";
import { ApiBase } from "./baseApi";

const PersonApiBase = `${apiAddress}/api/person/`;

class PersonApi extends ApiBase {
  static getSelf(): Promise<{
    person: IPerson;
    associations: IAssociation[];
    associationId?: string;
  }> {
    return PersonApi.get<IPerson>(PersonApiBase).then((resp) => {
      const person = resp.data.data || resp.data.data;
      if (!person)
        throw new Error(resp.data.error || "An unknown error occurred");
      try {
        const scope = Sentry.getCurrentScope()
        scope.setUser({ id: person._id, username: person.externalIdentifier });
      } catch (e) {
        console.error('Failed to set-up Sentry bug monitoring', e)
      }
      const associations = resp.data.associations || [];

      // @ts-ignore (I added this property on the getSelf call)
      const associationId = associations[0]?._id;

      return { person, associations, associationId };
    });
  }

  static getFullPersonById(
    personId: string,
  ): Promise<{ person: IPerson; associations: IAssociation[] }> {
    return PersonApi.get<IPerson>(`${PersonApiBase}${personId}/full`).then(
      (resp) => {
        if (!resp.data.data || !resp.data.associations) {
          throw new Error(
            "Action getFullPersonById failed for an unknown reason",
          );
        }
        return { person: resp.data.data, associations: resp.data.associations };
      },
    );
  }

  static getFullPersonByUsername(username: string): Promise<IPerson> {
    return PersonApi.get<IPerson>(`${PersonApiBase}${username}/username`).then(
      (resp) => resp.data.data,
    );
  }

  static getPersonsByIds(personIds: string[]): Promise<IPerson[]> {
    return PersonApi.post<IPerson[]>(`${PersonApiBase}multiple`, {
      ids: personIds,
    }).then((resp) => resp.data.data || []);
  }

  // static getPersonsByAssociation(associationId) {
  //     return PersonApi.get(PersonApiBase + `association/${associationId}`)
  //         .then(resp => (resp.data.data));
  // }

  static savePerson(person: Partial<IPerson>) {
    return PersonApi.post<IPerson>(PersonApiBase + person._id, {
      ...person,
    }).then((resp) => {
      if (!resp.data) throw new Error(resp.statusText);
      if (resp.data.success && resp) return resp.data.data;
      throw new Error(resp.data.error);
    });
  }

  static createPerson(person: Partial<IPerson>) {
    return PersonApi.post<IPerson>(PersonApiBase, { ...person }).then(
      (resp) => {
        if (!resp.data) throw new Error(resp.statusText);
        if (resp.data.success && resp) return resp.data.data;
        throw new Error(resp.data.error);
      },
    );
  }

  static associatePerson(
    personId: string,
    personAssociation: IPersonAssociation,
  ): Promise<IPerson> {
    return PersonApi.post<IPerson>(
      `${PersonApiBase + personId}/associate`,
      personAssociation,
    ).then((resp) => resp.data.data);
  }

  static deassociatePerson(
    personId: string,
    associationId: string,
  ): Promise<boolean> {
    return PersonApi.delete(
      `${PersonApiBase}${personId}/associate/${associationId}`,
    ).then((_) => true);
  }

  static getManageablePersons() {
    return PersonApi.get<{ persons: IPerson[]; associations: IAssociation[] }>(
      `${PersonApiBase}manageable`,
    ).then((resp) => resp.data.data);
  }

  static fuzzyTally(person: Partial<IPerson>, config?: AxiosRequestConfig) {
    return PersonApi.post<IFuzzyTally<IPerson>>(
      `${PersonApiBase}fuzzymatch`,
      person,
      config,
    ).then((resp) => resp.data.data);
  }

  static getActiveAtAssociation(associationId: string) {
    return PersonApi.get<IPerson[]>(
      `${PersonApiBase}association/${associationId}/active`,
    ).then((resp) => resp.data.data);
  }
}

export default PersonApi;
